import { makeObservable, observable, computed, action } from "mobx";

import { Vec2 } from "utils/math/vec2";
import { isWithinRange } from "utils/math/is-within-range";
import { roundToClosest } from "utils/math/round-to-closest";
import { hexToRgba } from "utils/styles/hex-to-rgba";
import { Webgl2 } from "../../../webgl2";
import { Webgl2Component } from "../../webgl2-component";
import { CircleComponent } from "./circle-component";
import { TextComponent } from "./text-component";

export class MeasurementComponent extends Webgl2Component {
  private _measurementHeight = 3;

  private readonly _startPos: Vec2;
  public get startPos() {
    return this._startPos.clone();
  }

  private readonly _endPos: Vec2;
  public get endPos() {
    return this._endPos.clone();
  }

  private _startCircle: CircleComponent;
  public get startCircle() {
    return this._startCircle;
  }

  private _endCircle: CircleComponent;
  public get endCircle() {
    return this._endCircle;
  }

  private _textComponent: TextComponent;
  public get textComponent() {
    return this._textComponent;
  }

  public get isValidPos() {
    return !this._startPos.equals(this._endPos);
  }

  private _straightenMode = false;
  public get straightenMode() {
    return this._straightenMode;
  }

  @observable
  private _text = "";

  @computed
  public get text() {
    return this._text;
  }

  @observable
  private _color = "";
  @computed
  public get color() {
    return this._color;
  }

  @observable
  private _colorIndex = 0;
  @computed
  public get colorIndex() {
    return this._colorIndex;
  }

  @observable
  private _tempColor = "";

  @computed
  public get tempColor() {
    return this._tempColor;
  }

  constructor(props: {
    color: string;
    text?: string;
    colorIndex?: number;
    straightenMode?: boolean;
    startPos?: Vec2;
    endPos?: Vec2;
  }) {
    const {
      color,
      text = "",
      colorIndex = 0,
      straightenMode = false,
      startPos = Vec2.zero(),
      endPos = Vec2.zero(),
    } = props;

    super();

    this._startPos = startPos.clone();
    this._endPos = endPos.clone();

    this._straightenMode = straightenMode;

    this._text = text;

    this._color = color;
    this._colorIndex = colorIndex;

    this._tempColor = this.color;

    const startCircle = new CircleComponent(this.color);
    startCircle.style.top = "50%";
    startCircle.style.left = "0";
    startCircle.style.translateY = "-50%";
    startCircle.style.translateX = "-50%";
    this._startCircle = startCircle;
    this.appendChild(startCircle);

    const endCircle = new CircleComponent(this.color);
    endCircle.style.top = "50%";
    endCircle.style.left = "100%";
    endCircle.style.translateY = "-50%";
    endCircle.style.translateX = "-50%";
    this._endCircle = endCircle;
    this.appendChild(endCircle);

    const textComponent = new TextComponent(text);
    this._textComponent = textComponent;
    this.appendChild(textComponent);

    this._calcTransform();

    makeObservable(this);
  }

  public clone() {
    return new MeasurementComponent({
      color: this._color,
      colorIndex: this._colorIndex,
      text: this._text,
      startPos: this._startPos,
      endPos: this._endPos,
    });
  }

  public setMeasurementSizes(height: number, fontSize: number) {
    this._startCircle.setHeightSize(height);
    this._endCircle.setHeightSize(height);
    this._textComponent.setFontSize(fontSize);
    this.setHeightSize(height);
  }

  public setHeightSize(height: number) {
    this._measurementHeight = Math.max(height, 1);
    this._calcTransform();
  }

  public changeStartPosition(startPos: Vec2) {
    if (this.straightenMode) {
      const dirTo = this.endPos.directionTo(startPos).negateY();
      const deg = dirTo.unitDegrees();
      const closestDeg = roundToClosest(deg, 90) % 360;
      const zeroOrOne = (closestDeg / 90) % 2;

      const x = zeroOrOne ? this.endPos.x : startPos.x;
      const y = zeroOrOne ? startPos.y : this.endPos.y;
      this._startPos.copy(x, y);
    } else {
      this._startPos.copy(startPos);
    }

    this._calcTransform();
  }

  public changeEndPosition(endPos: Vec2) {
    if (this.straightenMode) {
      const dirTo = this.startPos.directionTo(endPos).negateY();
      const deg = dirTo.unitDegrees();
      const closestDeg = roundToClosest(deg, 90) % 360;
      const zeroOrOne = (closestDeg / 90) % 2;

      const x = zeroOrOne ? this.startPos.x : endPos.x;
      const y = zeroOrOne ? endPos.y : this.startPos.y;
      this._endPos.copy(x, y);
    } else {
      this._endPos.copy(endPos);
    }

    this._calcTransform();
  }

  public changePositions(startPos: Vec2, endPos: Vec2) {
    this._startPos.copy(startPos);
    this._endPos.copy(endPos);
    this._calcTransform();
  }

  @action
  public changeColor(hex: string, index: number) {
    this._color = hex;
    this._colorIndex = index;
    this._tempColor = hex;
    this._startCircle.state.color = hex;
    this._endCircle.state.color = hex;
  }

  @action
  public changeTempColor(hex: string) {
    this._tempColor = hex;
    this._startCircle.state.color = hex;
    this._endCircle.state.color = hex;
  }

  @action
  public changeText(string: string) {
    this._text = string;
    this._textComponent.setText(string);
    this._calcTextTransform();
  }

  public setMeasurementsStraightenMode(value: boolean) {
    this._straightenMode = value;
  }

  @action
  private _calcTransform() {
    const distance = this._startPos.distanceTo(this._endPos);
    const direction = this._startPos.directionTo(this._endPos);
    const degrees = direction.clone().negateY().unitDegrees();

    const sizeToSet = new Vec2(distance, this._measurementHeight);
    const translationOrigin = new Vec2(0, this._measurementHeight * 0.5);
    const translationToSet = translationOrigin
      .negate()
      .add(this._startPos)
      .rotateDegrees(this._startPos, -degrees);

    this.transform.setSize(sizeToSet);
    this.transform.setTranslation(translationToSet);
    this.transform.setDegrees(degrees);

    this._calcTextTransform();
  }

  @action
  private _calcTextTransform() {
    if (!this.text && !this._textComponent.isEditing) return;
    const isWithinSteepAngle = isWithinRange(this.transform.degrees, 90, 270);
    this._textComponent.transform.calcDimensions({
      position: this.transform.size.getHalf(),
      rotation: isWithinSteepAngle ? 180 : 0,
      roundPosition: false,
    });
  }

  public getRenderData() {
    if (this._startPos.equals(this._endPos)) return;

    const material = Webgl2.materials.backgroundColor;
    // @ts-ignore
    material.setUniform("u_color", hexToRgba(this._tempColor, true));
    return { material };
  }
}

export default MeasurementComponent;
